/**
 * "Chi nhánh đang dùng" chung cho web quầy (ADR-101) — phần React. Hàm thuần nằm ở `chi-nhanh-dang-dung.ts`.
 *
 * `main.tsx` giữ MỘT nguồn (`useChiNhanhGoc`) rồi cấp xuống bằng `ChiNhanhCtx`; mọi màn gọi `useChiNhanh()`.
 * Chỉ báo duy nhất là dòng tên tiệm có sẵn ("TÓC NAM · Quận 1 ▾", chạm để đổi) — không thêm hàng, không thêm ô chọn.
 * Tiệm 0–1 chi nhánh (hoặc người dùng chỉ được giao một): `nhieu === false` → dòng tên y hệt trước, không đọc / ghi bộ nhớ.
 */
import { Check, ChevronDown, X } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Api, Branch, ToiLaAi } from './api.ts'
import { ApiFailure } from './api.ts'
import {
  chiNhanhMacDinhCuaTo,
  chonLucMo,
  coGhiNho,
  cungDanhSach,
  docNho,
  dsChonDuoc,
  ghiNho,
  idTuLuc,
  khoNhoMay,
  type TuyChonDoi,
  tenChiNhanh,
} from './chi-nhanh-dang-dung.ts'
import { T } from './text/vi.ts'
import { henThat, nenThuLai, taoBoThuLai } from './tu-phuc-hoi.ts'
import { To } from './ui.tsx'

export interface ChiNhanhGiaTri {
  /** Chi nhánh người này CHỌN ĐƯỢC; `null` = chưa tải xong. */
  ds: Branch[] | null
  /** Chi nhánh đang dùng; '' = chưa tải hoặc không có chi nhánh nào. */
  id: string
  ten: string
  /** Có > 1 chi nhánh chọn được — chỉ khi đó dòng tên tiệm mới thành nút. */
  nhieu: boolean
  /** Người dùng chọn (ghi nhớ), hoặc `{ nho: false }` cho chi nhánh tạm (BK-2). */
  doi(id: string, tuyChon?: TuyChonDoi): void
  /** Màn nào vừa tải được danh sách chi nhánh mới (Lịch tải lại + thử lại) báo về đây. */
  nhanDs(ds: Branch[]): void
  /** Tải lại danh sách (rời trang Cấu hình: chủ vừa thêm / ẩn chi nhánh). */
  taiLai(): void
}

const MAC_DINH: ChiNhanhGiaTri = {
  ds: null,
  id: '',
  ten: '',
  nhieu: false,
  doi: () => undefined,
  nhanDs: () => undefined,
  taiLai: () => undefined,
}

export const ChiNhanhCtx = createContext<ChiNhanhGiaTri>(MAC_DINH)

export function useChiNhanh(): ChiNhanhGiaTri {
  return useContext(ChiNhanhCtx)
}

/**
 * Nguồn duy nhất (gọi ở `BanLamViec`): tải danh sách một lần (lỗi mạng thì tự thử lại), nhớ chi nhánh theo TIỆM trên máy.
 * Không nghe sự kiện `storage` giữa các tab — tab đang mở tờ dở không bị đổi chi nhánh dưới tay.
 */
export function useChiNhanhGoc(api: Pick<Api, 'branches'>, toi: ToiLaAi): ChiNhanhGiaTri {
  const tiem = toi.tiem
  const [dsDayDu, setDsDayDu] = useState<Branch[] | null>(null)
  const [daNho, setDaNho] = useState<string | null>(null)
  const dsRef = useRef<Branch[] | null>(null)
  /** Số chi nhánh CHỌN ĐƯỢC hiện tại — `doi()` chỉ chạm bộ nhớ khi > 1. */
  const soChonRef = useRef(0)
  /** Đã đọc bộ nhớ chưa — chỉ đọc khi có > 1 chi nhánh chọn được, và chỉ một lần. */
  const daDoc = useRef(false)
  const nguoi = useMemo(
    () => ({ vai: toi.toi.vai, quyen: toi.quyen, chiNhanhCuaToi: toi.chiNhanhCuaToi }),
    [toi.toi.vai, toi.quyen, toi.chiNhanhCuaToi],
  )
  const nguoiRef = useRef(nguoi)
  nguoiRef.current = nguoi

  const nhanDs = useCallback(
    (moi: Branch[]) => {
      if (cungDanhSach(dsRef.current, moi)) return
      dsRef.current = moi
      setDsDayDu(moi)
      const soChon = dsChonDuoc(moi, nguoiRef.current).length
      soChonRef.current = soChon
      if (soChon > 1 && !daDoc.current) {
        daDoc.current = true
        setDaNho(docNho(khoNhoMay(), tiem))
      }
    },
    [tiem],
  )

  const doi = useCallback(
    (id: string, tuyChon?: TuyChonDoi) => {
      daDoc.current = true
      setDaNho(id)
      // Tiệm một chi nhánh không bao giờ chạm bộ nhớ; chi nhánh tạm (BK-2) không phải người dùng chọn thì không nhớ.
      if (coGhiNho(tuyChon) && soChonRef.current > 1) ghiNho(khoNhoMay(), tiem, id)
    },
    [tiem],
  )

  const thuLai = useRef(taoBoThuLai(henThat, Math.random))
  const daHuy = useRef(false)
  const taiLai = useCallback(() => {
    thuLai.current.huyHen()
    api
      .branches()
      .then((r) => {
        if (daHuy.current) return
        thuLai.current.datLai()
        nhanDs(r.branches)
      })
      .catch((e: unknown) => {
        if (daHuy.current) return
        const f = e instanceof ApiFailure ? e : null
        // Mất mạng / máy chủ lỗi: thử lại êm. Lỗi nghiệp vụ thì thôi — Lịch tự tải lại chi nhánh của nó.
        if (f !== null && nenThuLai(f.status)) thuLai.current.henLai(taiLai)
      })
  }, [api, nhanDs])

  useEffect(() => {
    daHuy.current = false
    const bo = thuLai.current
    taiLai()
    return () => {
      daHuy.current = true
      bo.huyHen()
    }
  }, [taiLai])

  return useMemo(() => {
    const dsChon = dsDayDu === null ? null : dsChonDuoc(dsDayDu, nguoi)
    soChonRef.current = dsChon?.length ?? 0
    const id = dsChon === null ? '' : idTuLuc(chonLucMo(dsChon, daNho, nguoi.chiNhanhCuaToi ?? []))
    return {
      ds: dsChon,
      id,
      ten: tenChiNhanh(dsChon, id),
      nhieu: dsChon !== null && dsChon.length > 1,
      doi,
      nhanDs,
      taiLai,
    }
  }, [dsDayDu, daNho, nguoi, doi, nhanDs, taiLai])
}

function ToChonChiNhanh({ onDong }: { onDong: () => void }) {
  const cn = useChiNhanh()
  const dsRef = useRef<HTMLUListElement>(null)
  // Mở tờ: đưa focus vào chi nhánh đang dùng (bàn phím / đọc màn hình không bị bỏ lại phía sau lớp phủ).
  useEffect(() => {
    dsRef.current?.querySelector<HTMLButtonElement>('.muc-cn.dang-chon, .muc-cn')?.focus()
  }, [])
  return (
    <To nhan={T.cnChonTieuDe} onDong={onDong}>
      <header className="to-dau">
        <h2 style={{ flex: 1 }}>{T.cnChonTieuDe}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        <ul ref={dsRef} className="ds-chon-cn">
          {(cn.ds ?? []).map((c) => {
            const dang = c.id === cn.id
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className={`muc-cn ${dang ? 'dang-chon' : ''}`}
                  aria-pressed={dang}
                  onClick={() => {
                    if (!dang) cn.doi(c.id)
                    onDong()
                  }}
                >
                  <span className="mc-ten">{c.name}</span>
                  {dang && (
                    <span className="mc-dang">
                      <Check size={18} aria-hidden="true" />
                      <span className="mc-dang-chu">{T.cnDangDung}</span>
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </To>
  )
}

/**
 * Dòng tên tiệm ở đầu mỗi màn. Tiệm 0–1 chi nhánh: ĐÚNG `<span class="ten-tiem">` như trước. Nhiều chi nhánh: thành nút
 * "TÊN TIỆM · Chi nhánh ▾", chạm → tờ chọn chi nhánh. Vùng chạm phủ cả khối tên + tiêu đề (CSS `::after`) nên không thêm chiều cao.
 */
export function DongTenTiem({
  tenTiem,
  theoChiNhanh = true,
}: {
  tenTiem: string
  /**
   * `false` ở màn số CẢ TIỆM (Sổ tiền › Hôm nay / Báo cáo / Hoá đơn / Bị huỷ…, Cấu hình, Việc của tôi): hậu tố sẽ bị hiểu là số đã
   * lọc theo chi nhánh — vẽ dòng tên thường như tiệm 1 chi nhánh.
   */
  theoChiNhanh?: boolean
}) {
  const cn = useChiNhanh()
  const [mo, setMo] = useState(false)
  const nutRef = useRef<HTMLButtonElement>(null)
  if (!theoChiNhanh || !cn.nhieu || cn.id === '') return <span className="ten-tiem">{tenTiem}</span>
  return (
    <>
      <button
        ref={nutRef}
        type="button"
        className="ten-tiem nut-ten-tiem"
        aria-label={T.cnNutAria(cn.ten)}
        aria-haspopup="dialog"
        title={cn.ten}
        onClick={() => setMo(true)}
      >
        <span className="tt-tiem">{tenTiem} ·</span>
        <span className="tt-cn">{cn.ten}</span>
        <ChevronDown size={18} aria-hidden="true" />
      </button>
      {/* Portal ra `body`: đầu trang dính có `backdrop-filter` nên là khung chứa của mọi phần tử `position: fixed` bên trong — tờ vẽ
          trong đó sẽ bị kẹt vào đầu trang thay vì phủ cả màn. */}
      {mo &&
        createPortal(
          <ToChonChiNhanh
            onDong={() => {
              setMo(false)
              nutRef.current?.focus()
            }}
          />,
          document.body,
        )}
    </>
  )
}

/**
 * Tờ ghi tiền có ô chọn chi nhánh riêng (Bán lẻ, Ghi khoản chi, Hoa hồng tiền mặt, Trả bo): chủ tiệm đôi khi ghi hộ chi
 * nhánh khác nên ô chọn GIỮ NGUYÊN — chỉ đổi mặc định thành chi nhánh đang dùng (không còn `branches[0]`).
 */
export function useChiNhanhCuaTo(): {
  ds: Branch[]
  nhieu: boolean
  branchId: string
  setBranchId: (id: string) => void
} {
  const cn = useChiNhanh()
  const [rieng, setRieng] = useState<string | null>(null)
  return {
    ds: cn.ds ?? [],
    nhieu: cn.nhieu,
    branchId: rieng ?? chiNhanhMacDinhCuaTo(cn.ds, cn.id),
    setBranchId: setRieng,
  }
}
